/**
 * 模組頁面代碼: 1340-UI
 * 模組說明: 帳號管理功能頁面
 * @module AccountManagementContainer
 */

// ----- import from local libraries -----
import {
  React,
  PropTypes,
  connect,
  BaseComponent,
  AccessModeEnums,
} from '../../components/BaseComponent';

// ----- import from local components -----
import Header from '../../features/main-frame/components/Header';
import Footer from '../../features/main-frame/components/Footer';
import Sidebar from '../../features/main-frame/components/Sidebar';
import AccountManagementPage from './components/AccountManagementPage';
import UserModal from './components/UserModal';
import UserPasswordModal from './components/UserPasswordModal';
import {AlertModal, AlertModalModeEnums} from '../../components/AlertModal';
import {MessageModal} from '../../components/MessageModal';
import enumList from 'web/utils/enumList';

// ----- import from local actions -----
import {fetchBuildingSelection} from '../../redux/actions/BuildingActions';
import {fetchRoleSelection} from '../../redux/actions/RoleActions';
import {
  addPosition,
  editPosition,
  editPositionStatus,
  fetchPosition,
  fetchPositionList,
  fetchPositionSelection,
} from '../../redux/actions/PositionActions';
import {
  addUser,
  editUser,
  editUserStatus,
  editUserPassword,
  deleteUser,
  fetchUserList,
} from '../../redux/actions/UserActions';
import {fetchAvailableEmployeeList} from 'web/redux/actions/EmployeeActions';
import {
  clearSelectedEmployee,
  setEditModalEmployees,
} from 'web/redux/slices/EmployeeSlice';

// ----- constants and enums -----
const PositionModalModeEnums = {
  NEW: 'new',
  EDIT: 'edit',
};

const UserModalModeEnums = {
  NEW: 'new',
  EDIT: 'edit',
};

const ACTION_TARGET_USER = 'user';
const ACTION_TARGET_POSITION = 'position';

const RESPONSE_OK = 200;
const TITLE_POSITION_STATUS = '修改職位狀態';
const TITLE_USER_DELETE = '刪除使用者';
const TITLE_USER_STATUS = '修改使用者狀態';

/**
 *
 */
class AccountManagementContainer extends BaseComponent {
  /**
   *
   */
  constructor(props) {
    super(props);
    // Ref
    this.userModalRef = React.createRef();
    this.userPasswordModalRef = React.createRef();
    this.positionListModalRef = React.createRef();
    this.positionModalRef = React.createRef();
    this.alertModalRef = React.createRef();
    this.messageModalRef = React.createRef();
    this.dtableRef = React.createRef();
    this.userModal = null;
    this.userPasswordModal = null;
    this.positionListModal = null;
    this.positionModal = null;
    this.alertModal = null;
    this.messageModal = null;

    this.state = {
      needAccountManagementLoad: false,
      needPositionListLoad: false,
      positionModalMode: PositionModalModeEnums.NEW,
      userModalMode: UserModalModeEnums.NEW,
      actionMode: AlertModalModeEnums.ENABLE,
      actionTarget: '',
      user: null,
      position: null,
      alertMessage: '',
      actionIds: [],
      message: {mode: enumList.messageModalMode.error, title: '', body: ''},
      search: {buildingId: 0, userName: '', roleId: 0},
    };
    this.toggleUserModal = this.toggleUserModal.bind(this);
    this.toggleUserPasswordModal = this.toggleUserPasswordModal.bind(this);
    this.togglePositionListModal = this.togglePositionListModal.bind(this);
    this.togglePositionModal = this.togglePositionModal.bind(this);
    this.toggleAlertModal = this.toggleAlertModal.bind(this);
    this.toggleMessageModal = this.toggleMessageModal.bind(this);
    this.onConfirm = this.onConfirm.bind(this);
  }

  /**
   *
   */
  toggleUserModal(isShow, mode = UserModalModeEnums.NEW, index = null) {
    if (this.userModal) {
      if (mode === UserModalModeEnums.EDIT) {
        this.setState({
          user: JSON.parse(JSON.stringify(this.props.userList[index])),
        });
      } else {
        this.setState({user: null});
      }

      if (isShow) {
        this.userModal.show();
      } else {
        this.userModal.hide();
      }
      this.setState({userModalMode: mode});
    }
  }

  /**
   *
   */
  toggleUserPasswordModal(isShow, index = null) {
    if (this.userPasswordModal) {
      if (isShow && index !== null) {
        this.setState({user: this.props.userList[index]});
        this.userPasswordModal.show();
      } else {
        this.userPasswordModal.hide();
      }
    }
  }

  /**
   *
   */
  togglePositionListModal(isShow) {
    if (this.positionListModal) {
      if (isShow) {
        this.positionListModal.show();
      } else {
        this.positionListModal.hide();
      }
    }
  }

  /**
   *
   */
  togglePositionModal(isShow, mode = PositionModalModeEnums.NEW, index = null) {
    if (this.positionModal) {
      if (mode === PositionModalModeEnums.EDIT) {
        this.setState({position: this.props.positionList[index]});
      } else {
        this.setState({position: null});
      }

      if (isShow) {
        this.positionModal.show();
      } else {
        this.positionModal.hide();
      }
      this.setState({positionModalMode: mode});
    }
  }

  /**
   *
   */
  toggleAlertModal(
    isShow,
    mode = AlertModalModeEnums.ENABLE,
    target,
    message = '',
    usernames = [],
  ) {
    if (this.alertModal) {
      if (isShow) {
        let alertMessage = message;
        if (alertMessage === '') {
          const length = usernames.length;
          if (mode === AlertModalModeEnums.ENABLE) {
            alertMessage = `啟用 [ ${length} ] 筆資料`;
          } else if (mode === AlertModalModeEnums.DISABLE) {
            alertMessage = `停用 [ ${length} ] 筆資料`;
          } else if (mode === AlertModalModeEnums.DELETE) {
            alertMessage = `刪除 [ ${length} ] 筆資料`;
          }
        }
        this.setState({actionMode: mode});
        this.setState({alertMessage: alertMessage});
        this.setState({actionTarget: target});
        this.setState({actionIds: usernames});
        this.alertModal.show();
      } else {
        this.alertModal.hide();
      }
    }
  }

  /**
   *
   */
  toggleMessageModal(
    isShow,
    mode = enumList.messageModalMode.error,
    title = '',
    message = '',
  ) {
    if (this.messageModal) {
      if (isShow) {
        let body = message;
        const statusCode = message?.code || '';
        if (statusCode !== '') {
          body = `[ ${statusCode} ] ${message.message}`;
        }
        this.setState({message: {mode: mode, title: title, body: body}});
        this.messageModal.show();
      } else {
        this.messageModal.hide();
      }
    }
  }

  /**
   *
   */
  onConfirm() {
    const actionTarget = this.state.actionTarget;
    const actionMode = this.state.actionMode;
    const actionIds = this.state.actionIds;

    if (actionTarget === ACTION_TARGET_POSITION) {
      const positionId = actionIds[0];
      const status = actionMode === AlertModalModeEnums.ENABLE ? 'A' : 'I';
      this.props.editPositionStatus(positionId, status).then((response) => {
        const statusCode = response.payload.code;
        if (statusCode !== RESPONSE_OK) {
          this.toggleAlertModal(false);
          this.toggleMessageModal(
            true,
            enumList.messageModalMode.error,
            TITLE_POSITION_STATUS,
            response.payload,
          );
        } else {
          this.props.fetchPositionSelection();
          this.props.fetchPositionList().then(() => {
            this.setState({needPositionListLoad: true});
            this.toggleAlertModal(false);
            this.togglePositionListModal(true);
          });
        }
      });
    } else if (actionTarget === ACTION_TARGET_USER) {
      const userIdsString = actionIds.join(',');
      if (actionMode === AlertModalModeEnums.DELETE) {
        this.props.deleteUser(userIdsString).then((response) => {
          const statusCode = response.payload.code;
          if (statusCode !== RESPONSE_OK) {
            this.toggleAlertModal(false);
            this.toggleMessageModal(
              true,
              enumList.messageModalMode.error,
              TITLE_USER_DELETE,
              response.payload,
            );
          } else {
            const {username, roleId} = this.state.search;
            this.props.fetchUserList(username, roleId).then(() => {
              this.setState({needAccountManagementLoad: true});
              this.toggleAlertModal(false);
            });
          }
        });
      } else if (
        actionMode === AlertModalModeEnums.ENABLE ||
        actionMode === AlertModalModeEnums.DISABLE
      ) {
        const status = actionMode === AlertModalModeEnums.ENABLE ? 'A' : 'I';
        this.props.editUserStatus(actionIds, status).then((response) => {
          const statusCode = response.payload.code;
          if (statusCode !== RESPONSE_OK) {
            this.toggleAlertModal(false);
            this.toggleMessageModal(
              true,
              enumList.messageModalMode.error,
              TITLE_USER_STATUS,
              response.payload,
            );
          } else {
            const {username, roleId} = this.state.search;
            setTimeout(() => {
              this.props.fetchUserList(username, roleId).then(() => {
                this.setState({needAccountManagementLoad: true});
                this.toggleAlertModal(false);
              });
            }, 10);
          }
        });
      }
    }
  }

  /**
   *
   */
  componentDidMount() {
    // 頁面所需資料初始化
    this.props.fetchRoleSelection();
    this.dtableRef.current.processing(true);
    this.props.fetchUserList('', 0).then(() => {
      this.setState({needAccountManagementLoad: true});
    });
    this.props.fetchPositionList().then(() => {
      this.setState({needPositionListLoad: true});
    });
    this.userModal = new bootstrap.Modal(this.userModalRef.current);
    this.userPasswordModal = new bootstrap.Modal(
      this.userPasswordModalRef.current,
    );
    this.alertModal = new bootstrap.Modal(this.alertModalRef.current);
    this.messageModal = new bootstrap.Modal(this.messageModalRef.current);
  }

  /**
   *
   */
  componentDidUpdate(prevProps, prevState) {}

  /**
   *
   */
  componentWillUnmount() {
    this.toggleUserModal(false);
    this.toggleUserPasswordModal(false);
    // this.togglePositionListModal(false);
    // this.togglePositionModal(false);
    this.toggleAlertModal(false);
    this.toggleMessageModal(false);
  }

  /**
   *
   */
  render() {
    return (
      <React.Fragment>
        <div className="m__wrap">
          <Header />
          <div className="m__contents">
            <Sidebar />
            <AccountManagementPage
              {...this.props}
              needAccountManagementLoad={this.state.needAccountManagementLoad}
              setNeedAccountManagementLoad={(bLoad) =>
                this.setState({needAccountManagementLoad: bLoad})
              }
              onNewUser={() =>
                this.toggleUserModal(true, UserModalModeEnums.NEW)
              }
              onEditUser={(index) =>
                this.toggleUserModal(true, UserModalModeEnums.EDIT, index)
              }
              onEditPassword={(index) =>
                this.toggleUserPasswordModal(true, index)
              }
              onEditUserStatus={(mode, message, usernames) =>
                this.toggleAlertModal(true, mode, ACTION_TARGET_USER, message, [
                  usernames,
                ])
              }
              onDeleteUser={(message, userId) =>
                this.toggleAlertModal(
                  true,
                  AlertModalModeEnums.DELETE,
                  ACTION_TARGET_USER,
                  message,
                  [userId],
                )
              }
              onBatchHandle={(mode, userIds) =>
                this.toggleAlertModal(
                  true,
                  mode,
                  ACTION_TARGET_USER,
                  '',
                  userIds,
                )
              }
              setSearchUser={(username, roleId) => {
                this.setState({search: {username: username, roleId: roleId}});
              }}
              onPositionList={() => this.togglePositionListModal(true)}
              dtableRef={this.dtableRef}
            />
          </div>
          <Footer />
        </div>
        {/* <PositionListModal
          {...this.props}
          ref={this.positionListModalRef}
          checkPermission={this.checkPermission.bind(this)}
          needPositionListLoad={this.state.needPositionListLoad}
          setNeedPositionListLoad={(bLoad) => this.setState({ needPositionListLoad: bLoad })}
          onNewPosition={() => {
            this.togglePositionModal(true, PositionModalModeEnums.NEW);
            this.togglePositionListModal(false);
          }}
          onEditPosition={(index) => {
            this.togglePositionModal(true, PositionModalModeEnums.EDIT, index);
            this.togglePositionListModal(false);
          }}
          onEditPositionStatus={(mode, message, positionId) => {
            this.toggleAlertModal(true, mode, ACTION_TARGET_POSITION, message, [positionId]);
            this.togglePositionListModal(false);
          }}
        /> */}
        <UserModal
          {...this.props}
          ref={this.userModalRef}
          mode={this.state.userModalMode}
          user={this.state.user}
          searchUser={this.state.search}
          setNeedAccountManagementLoad={(bLoad) =>
            this.setState({needAccountManagementLoad: bLoad})
          }
          onErrorHandle={(title, message) =>
            this.toggleMessageModal(
              true,
              enumList.messageModalMode.error,
              title,
              message,
            )
          }
        />
        <UserPasswordModal
          {...this.props}
          ref={this.userPasswordModalRef}
          user={this.state.user}
          onErrorHandle={(title, message) =>
            this.toggleMessageModal(
              true,
              enumList.messageModalMode.error,
              title,
              message,
            )
          }
        />
        {/* <PositionModal
          {...this.props}
          ref={this.positionModalRef}
          mode={this.state.positionModalMode}
          position={this.state.position}
          setNeedPositionListLoad={(bLoad) => {
            this.setState({ needPositionListLoad: bLoad });
            this.togglePositionListModal(true);
          }}
          onErrorHandle={(title, message) => this.toggleMessageModal(true, enumList.messageModalMode.error, title, message)}
        /> */}
        <AlertModal
          ref={this.alertModalRef}
          mode={this.state.actionMode}
          message={this.state.alertMessage}
          onConfirm={() => this.onConfirm()}
          onCancel={() => this.setState({needAccountManagementLoad: true})}
        />
        <MessageModal
          ref={this.messageModalRef}
          mode={this.state.message.mode}
          title={this.state.message.title}
          message={this.state.message.body}
        />
      </React.Fragment>
    );
  }
}

AccountManagementContainer.propTypes = {
  userPermissions: PropTypes.array,
  buildingSelection: PropTypes.array,
  roleSelection: PropTypes.array,
  positionSelection: PropTypes.array,
  postionList: PropTypes.array,
  userList: PropTypes.array,
  employeeSelection: PropTypes.array,
};

AccountManagementContainer.defaultProps = {
  userPermissions: [],
  buildingSelection: [],
  roleSelection: [],
  positionSelection: [],
  positionList: [],
  userList: [],
  employeeSelection: [],
};

/**
 *
 */
function mapStateToProps(state) {
  return {
    userPermissions: JSON.parse(state.access.userPermissions),
    buildingSelection: state.building.buildingSelection.data,
    roleSelection: state.role.roleSelection.data,
    positionSelection: state.position.positionSelection.data,
    positionList: state.position.positionList.data,
    userList: state.user.userList.data,
    employeeSelection: state.employee.availableEmployeeList.data,
  };
}

/**
 *
 */
function mapDispatchToProps(dispatch) {
  return {
    fetchBuildingSelection: () => {
      return dispatch(fetchBuildingSelection());
    },
    fetchRoleSelection: () => {
      return dispatch(fetchRoleSelection());
    },
    addPosition: (position) => {
      return dispatch(addPosition(position));
    },
    editPosition: (position) => {
      return dispatch(editPosition(position));
    },
    editPositionStatus: (positionId, status) => {
      return dispatch(editPositionStatus({positionId, status}));
    },
    fetchPosition: (positionId) => {
      return dispatch(fetchPosition(positionId));
    },
    fetchPositionSelection: () => {
      return dispatch(fetchPositionSelection());
    },
    fetchPositionList: () => {
      return dispatch(fetchPositionList());
    },
    fetchUserList: (username, roleId) => {
      return dispatch(fetchUserList({username, roleId}));
    },
    fetchAvailableEmployeeList: () => {
      return dispatch(fetchAvailableEmployeeList());
    },
    editEmployeeList: ({employeeId, employeeName}) => {
      return dispatch(setEditModalEmployees({employeeId, employeeName}));
    },
    addUser: (user) => {
      return dispatch(addUser(user));
    },
    editUser: (user) => {
      return dispatch(editUser(user));
    },
    editUserStatus: (usernames, status) => {
      return dispatch(editUserStatus({usernames, status}));
    },
    editUserPassword: (username, newPassword) => {
      return dispatch(editUserPassword({username, newPassword}));
    },
    deleteUser: (userIds) => {
      return dispatch(deleteUser(userIds));
    },
    clearSelectedEmployee: () => {
      return dispatch(clearSelectedEmployee());
    },
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AccountManagementContainer);
